<template>
  <div class="register-container">
    <div class="yarn-ball-decoration"></div>
    <div class="yarn-ball-decoration secondary"></div>
    <div class="register-form-wrapper">
      <div class="header-area">
        <el-button 
          :icon="ArrowLeft" 
          type="default" 
          @click="goBack"
          plain
          circle
          class="back-button"
        ></el-button>
        <h2 class="form-title">🧵 注册新账号</h2>
      </div>
      <el-form 
        :model="registerForm" 
        :rules="rules" 
        ref="registerFormRef"
        label-width="80px" 
        class="register-form"
      >
        <el-form-item label="用户名" prop="username">
          <el-input 
            v-model="registerForm.username" 
            :prefix-icon="User"
            placeholder="请输入用户名"
            size="large"
          />
        </el-form-item>

        <el-form-item label="密码" prop="password">
          <el-input 
            v-model="registerForm.password" 
            type="password"
            :prefix-icon="Lock"
            placeholder="请输入密码"
            size="large"
            show-password
          />
        </el-form-item>

        <el-form-item label="确认密码" prop="confirmPassword">
          <el-input 
            v-model="registerForm.confirmPassword" 
            type="password"
            :prefix-icon="Lock"
            placeholder="请再次输入密码"
            size="large"
            show-password
          />
        </el-form-item>

        <el-form-item label="邮箱" prop="email">
          <el-input 
            v-model="registerForm.email" 
            :prefix-icon="Message"
            placeholder="请输入邮箱地址（可选）"
            size="large"
          />
        </el-form-item>

        <el-form-item label="手机号" prop="phone">
          <el-input 
            v-model="registerForm.phone" 
            :prefix-icon="Phone"
            placeholder="请输入手机号码（可选）"
            size="large"
          />
        </el-form-item>

        <el-form-item>
          <el-button 
            type="primary" 
            @click="handleRegister" 
            :loading="loading"
            class="register-button"
            size="large"
          >
            注册
          </el-button>
        </el-form-item>

        <div class="login-link">
          已有账号？
          <el-link type="primary" @click="goToLogin">立即登录</el-link>
        </div>
      </el-form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage } from 'element-plus';
import { User, Lock, Message, Phone, ArrowLeft } from '@element-plus/icons-vue';
import { register } from '@/user/api/auth';

interface RegisterForm {
  username: string;
  password: string;
  confirmPassword: string;
  email?: string;
  phone?: string;
}

const router = useRouter();
const registerFormRef = ref();
const loading = ref(false);

const registerForm = reactive<RegisterForm>({
  username: '',
  password: '',
  confirmPassword: '',
  email: '',
  phone: ''
});

const validateConfirmPassword = (rule: any, value: string, callback: any) => {
  if (value === '') {
    callback(new Error('请再次输入密码'));
  } else if (value !== registerForm.password) {
    callback(new Error('两次输入的密码不一致'));
  } else {
    callback();
  }
};

const validateEmail = (rule: any, value: string, callback: any) => {
  if (value === '') {
    callback();
  } else {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      callback(new Error('请输入正确的邮箱格式'));
    } else {
      callback();
    }
  }
};

const validatePhone = (rule: any, value: string, callback: any) => {
  if (value === '') {
    callback();
  } else {
    const phoneRegex = /^1[3-9]\d{9}$/;
    if (!phoneRegex.test(value)) {
      callback(new Error('请输入正确的手机号码'));
    } else {
      callback();
    }
  }
};

const rules = {
  username: [
    { required: true, message: '请输入用户名', trigger: 'blur' },
    { min: 3, max: 20, message: '用户名长度在3-20个字符之间', trigger: 'blur' }
  ],
  password: [
    { required: true, message: '请输入密码', trigger: 'blur' },
    { min: 6, max: 20, message: '密码长度在6-20个字符之间', trigger: 'blur' }
  ],
  confirmPassword: [
    { validator: validateConfirmPassword, trigger: 'blur' }
  ],
  email: [
    { validator: validateEmail, trigger: 'blur' }
  ],
  phone: [
    { validator: validatePhone, trigger: 'blur' }
  ]
};

const handleRegister = async () => {
  // 验证表单
  try {
    await registerFormRef.value.validate();
  } catch {
    ElMessage.error('请检查输入信息');
    return;
  }

  // 开始注册
  loading.value = true;
  try {
    const { confirmPassword, ...params } = registerForm;
    const response = await register(params);
    
    if (response.code === 1) {
      ElMessage.success('注册成功！');
      // 注册成功后跳转到登录页
      setTimeout(() => {
        router.push('/login');
      }, 1000);
    } else {
      ElMessage.error(response.msg || '注册失败');
    }
  } catch (error: any) {
    console.error('注册失败:', error);
    ElMessage.error(error.message || '注册失败，请稍后重试');
  } finally {
    loading.value = false;
  }
};

const goToLogin = () => {
  router.push('/login');
};

const goBack = () => {
  // 尝试返回上一页，如果不存在则跳转到首页
  if (window.history.length > 1) {
    router.go(-1);
  } else {
    router.push('/');
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700&family=Quicksand:wght@500;600&display=swap');

.register-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-image: url('../../../background/background.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
  overflow: hidden;
  font-family: 'Nunito', 'Quicksand', 'Open Sans', sans-serif;
}

.yarn-ball-decoration {
  position: absolute;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: linear-gradient(135deg, #d4a976 0%, #cbb394 100%);
  opacity: 0.2;
  z-index: 0;
}

.yarn-ball-decoration:nth-child(1) {
  top: 10%;
  left: 5%;
  width: 150px;
  height: 150px;
  animation: float 8s infinite ease-in-out;
}

.yarn-ball-decoration.secondary {
  bottom: 15%;
  right: 7%;
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, #a8977a 0%, #8b6d4b 100%);
  animation: float 10s infinite ease-in-out;
}

@keyframes float {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(20px, 20px); }
}

.header-area {
  display: flex;
  align-items: center;
  margin-bottom: 30px;
  position: relative;
}

.back-button {
  margin-right: 15px;
  border: none;
  background: rgba(212, 169, 118, 0.1);
  color: #8b6d4b;
  transition: all 0.3s;
}

.back-button:hover {
  background: rgba(212, 169, 118, 0.2);
  transform: scale(1.05);
}

.register-form-wrapper {
  width: 100%;
  max-width: 450px;
  padding: 30px;
  border-radius: 20px;
  box-shadow: 0 15px 35px rgba(139, 109, 75, 0.2);
  border: 1px solid rgba(212, 169, 118, 0.3);
  background: rgba(255, 250, 240, 0.3);
  backdrop-filter: blur(10px);
  position: relative;
  z-index: 10;
  border: 1px solid #e9dcc8;
  /* 添加玻璃流动动画效果 */
  animation: glassFlow 15s ease-in-out infinite alternate;
}

/* 新增玻璃流动动画 */
@keyframes glassFlow {
  0% {
    background: rgba(255, 250, 240, 0.25);
    box-shadow: 0 10px 30px rgba(139, 109, 75, 0.15);
  }
  50% {
    background: rgba(255, 252, 245, 0.35);
    box-shadow: 0 15px 40px rgba(139, 109, 75, 0.25);
  }
  100% {
    background: rgba(254, 248, 235, 0.28);
    box-shadow: 0 12px 35px rgba(139, 109, 75, 0.2);
  }
}

.form-title {
  text-align: center;
  margin: 0;
  flex: 1;
  color: #5a3e36;
  font-family: 'Quicksand', cursive;
  font-weight: 600;
  font-size: 24px;
}

.register-form {
  width: 100%;
}

.register-form .el-form-item {
  margin-bottom: 20px;
}

.register-form .el-input__wrapper {
  border-radius: 12px;
  border: 1px solid #d4c19c;
  padding: 0 15px;
  background: rgba(255, 255, 255, 0.6);
}

.register-button {
  width: 100%;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(to right, #d4a976, #cbb394);
  border: none;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 1px;
  transition: all 0.3s;
}

.register-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(212, 169, 118, 0.4);
}

.register-button:active {
  transform: translateY(0);
}

.login-link {
  text-align: center;
  margin-top: 20px;
  color: #a8977a;
}

.login-link p {
  margin: 0;
  font-size: 14px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .register-container {
    padding: 20px;
  }
  
  .register-form-wrapper {
    width: 100%;
    max-width: 400px;
    padding: 25px 20px;
  }
  
  .form-title {
    font-size: 20px;
  }
  
  .yarn-ball-decoration {
    display: none;
  }
}

@media (max-width: 480px) {
  .register-form-wrapper {
    padding: 20px 15px;
  }
  
  .form-title {
    font-size: 18px;
  }
}
</style>